import {
  dollarSavings,
  extractCarrierCellInputs,
  periodDollarSavings,
  periodVariance,
  totalAnnualPremiumForCarrier,
  totalAnnualPremiumForPeriod,
  totalDollarSavingsForCarrier,
  totalDollarSavingsForPeriod,
  totalPremiumWithCredits,
  totalVarianceForCarrier,
  totalVarianceForPeriod,
  variance,
} from "@platform/formatting-utils/financials";

import { financialsRowKinds } from "./types";

import type {
  CarrierCellInputs,
  CellPair,
} from "@platform/formatting-utils/financials";
import type {
  FinancialsGridContext,
  FinancialsRow,
  FinancialsRowKind,
} from "./types";
import type {
  PeriodColumns,
  PeriodKey,
  TotalsBlock,
  TotalsBlockCarrierNotes,
  TotalsBlockPeriodNotes,
} from "@platform/api";

const getBlockNotes = (
  block: TotalsBlock,
): {
  periodNotes?: TotalsBlockPeriodNotes;
  carrierNotes?: TotalsBlockCarrierNotes;
} => (block.kind === "custom" ? block : block);

const getTotalNotes: GetCellValue = (context, row, column) => {
  const blockId = row.blockId ?? "default";
  const block = context.blockById.get(blockId);
  if (!block) return null;

  const { periodNotes, carrierNotes } = getBlockNotes(block);

  if (column.kind === "period") {
    return periodNotes?.[column.period] ?? null;
  }

  return carrierNotes?.[column.carrierId] ?? null;
};

/**
 * Identifies which kind of column a valueGetter is running against.
 * Closure-captured per column when the colDef is built so the per-row
 * dispatch only has to switch on row kind.
 */
export type ColumnContext =
  | { kind: "period"; period: PeriodKey }
  | { kind: "carrier"; carrierId: string };

export type CellValue = number | string | CellPair | null;

type GetCellValue = (
  context: FinancialsGridContext,
  row: FinancialsRow,
  column: ColumnContext,
) => CellValue;

const lookupCoverageId = (
  context: FinancialsGridContext,
  row: FinancialsRow,
): string | undefined => context.coverageByRowId.get(row.rowId);

const getAnnualPremium: GetCellValue = (context, row, column) => {
  const coverageId = lookupCoverageId(context, row);
  if (coverageId === undefined) return null;

  if (column.kind === "period") {
    return (
      context.payload.periods[column.period].coverages[coverageId]
        ?.annualPremium ?? null
    );
  }

  const carrier = context.carrierById.get(column.carrierId);
  if (!carrier) return null;

  return carrier.coverages[coverageId]?.annualPremium ?? null;
};

const getCarrierCellPair = (
  context: FinancialsGridContext,
  row: FinancialsRow,
  column: ColumnContext,
  calc: (inputs: CarrierCellInputs) => CellPair,
): CellPair | null => {
  if (column.kind !== "carrier") return null;
  const coverageId = lookupCoverageId(context, row);
  if (coverageId === undefined) return null;

  const coverage = context.coverageById.get(coverageId);
  const carrier = context.carrierById.get(column.carrierId);
  if (!coverage || !carrier) return null;

  return calc(
    extractCarrierCellInputs(context.payload.periods, coverage, carrier),
  );
};

const computeVariance = (inputs: CarrierCellInputs): CellPair =>
  variance(inputs);

const computeSavings = (inputs: CarrierCellInputs): CellPair =>
  dollarSavings(inputs);

const getPeriodCellPair = (
  context: FinancialsGridContext,
  row: FinancialsRow,
  column: ColumnContext,
  calc: (
    periods: PeriodColumns,
    periodKey: PeriodKey,
    coverageId: string,
    isVirgin: boolean,
  ) => CellPair | null,
): CellPair | null => {
  if (column.kind !== "period") return null;
  const coverageId = lookupCoverageId(context, row);
  if (coverageId === undefined) return null;

  const coverage = context.coverageById.get(coverageId);
  if (!coverage) return null;

  return calc(
    context.payload.periods,
    column.period,
    coverageId,
    coverage.isVirgin,
  );
};

const getVariance: GetCellValue = (context, row, column) =>
  column.kind === "carrier"
    ? getCarrierCellPair(context, row, column, computeVariance)
    : getPeriodCellPair(context, row, column, periodVariance);

const getDollarSavings: GetCellValue = (context, row, column) =>
  column.kind === "carrier"
    ? getCarrierCellPair(context, row, column, computeSavings)
    : getPeriodCellPair(context, row, column, periodDollarSavings);

const getNotes: GetCellValue = (context, row, column) => {
  const coverageId = lookupCoverageId(context, row);
  if (coverageId === undefined) return null;

  if (column.kind === "period") {
    return (
      context.payload.periods[column.period].coverages[coverageId]?.note ?? null
    );
  }

  const carrier = context.carrierById.get(column.carrierId);
  if (!carrier) return null;

  return carrier.coverages[coverageId]?.note ?? null;
};

const EMPTY_SET: ReadonlySet<string> = new Set();

export const lookupBlockCoverageIds = (
  context: FinancialsGridContext,
  row: FinancialsRow,
): ReadonlySet<string> =>
  context.coverageIdsByBlock.get(row.blockId ?? "default") ?? EMPTY_SET;

const getTotalAnnualPremium: GetCellValue = (context, row, column) => {
  const coverageIds = lookupBlockCoverageIds(context, row);

  return column.kind === "period"
    ? totalAnnualPremiumForPeriod(context.payload, column.period, coverageIds)
    : totalAnnualPremiumForCarrier(
        context.payload,
        column.carrierId,
        coverageIds,
      );
};

const getTotalVariance: GetCellValue = (context, row, column) => {
  const coverageIds = lookupBlockCoverageIds(context, row);

  return column.kind === "carrier"
    ? totalVarianceForCarrier(context.payload, column.carrierId, coverageIds)
    : totalVarianceForPeriod(context.payload, column.period, coverageIds);
};

const getTotalDollarSavings: GetCellValue = (context, row, column) => {
  const coverageIds = lookupBlockCoverageIds(context, row);

  return column.kind === "carrier"
    ? totalDollarSavingsForCarrier(
        context.payload,
        column.carrierId,
        coverageIds,
      )
    : totalDollarSavingsForPeriod(context.payload, column.period, coverageIds);
};

const getTotalCredits: GetCellValue = (context, row, column) => {
  const blockId = row.blockId ?? "default";
  const block = context.blockById.get(blockId);
  if (!block) return null;

  const { creditsRow } = block;

  if (column.kind === "period") {
    return creditsRow.periodValues?.[column.period] ?? null;
  }

  return creditsRow.carrierValues?.[column.carrierId] ?? null;
};

const getTotalPremiumWithCredits: GetCellValue = (context, row, column) => {
  const totalPremium = getTotalAnnualPremium(context, row, column);
  if (typeof totalPremium !== "number") return null;

  const credits = getTotalCredits(context, row, column);

  return totalPremiumWithCredits(
    totalPremium,
    typeof credits === "number" ? credits : null,
  );
};

/**
 * Lookup table replacing what would otherwise be a switch on row kind.
 * Coverage banner rows render full-width and don't produce a per-cell
 * value; their entry exists for completeness so callers don't have to
 * special-case the lookup.
 */
const valueByRowKind: Record<FinancialsRowKind, GetCellValue> = {
  [financialsRowKinds.COVERAGE_HEADER]: () => null,
  [financialsRowKinds.TOTALS_HEADER]: () => null,
  [financialsRowKinds.ANNUAL_PREMIUM]: getAnnualPremium,
  [financialsRowKinds.VARIANCE]: getVariance,
  [financialsRowKinds.DOLLAR_SAVINGS]: getDollarSavings,
  [financialsRowKinds.NOTES]: getNotes,
  [financialsRowKinds.TOTAL_ANNUAL_PREMIUM]: getTotalAnnualPremium,
  [financialsRowKinds.TOTAL_VARIANCE]: getTotalVariance,
  [financialsRowKinds.TOTAL_DOLLAR_SAVINGS]: getTotalDollarSavings,
  [financialsRowKinds.TOTAL_NOTES]: getTotalNotes,
  [financialsRowKinds.TOTAL_CREDITS]: getTotalCredits,
  [financialsRowKinds.TOTAL_PREMIUM_WITH_CREDITS]: getTotalPremiumWithCredits,
};

export const getCellValue = (
  context: FinancialsGridContext,
  row: FinancialsRow,
  column: ColumnContext,
): CellValue => valueByRowKind[row.kind](context, row, column);
